// DD-3793: file uploads to denthub-documents that never got an answer the app can act on.
// The browser reports most of them as a bare axios "Network Error" (no response at all): the
// connection dropped (client offline, laptop asleep), or the server cut a slow upload off and
// answered without CORS headers, which the browser hides from the page. None of them is a bug in
// the UI, so they get the feedback dialog with a retry, not the full-page error screen.
import axios from 'axios';
import { formatBytes } from 'utils/format';

export type UploadFailureKind = 'CONNECTION' | 'TOO_LARGE' | 'SERVER';

/** One upload request, measured while it runs, so a failure can say how far it got. */
export type UploadAttempt = {
    files: File[];
    startedAt: number;
    /** Bytes the browser had sent when the request ended (from onUploadProgress). */
    sentBytes: number;
};

// The drop areas hand over a FileList despite their File[] type, so copy it into an array.
export const startUploadAttempt = (files: ArrayLike<File>): UploadAttempt => ({
    files: Array.from(files),
    startedAt: Date.now(),
    sentBytes: 0,
});

export const getTotalBytes = (files: File[]): number => files.reduce((total, file) => total + (file.size || 0), 0);

/**
 * Why the transfer failed, or undefined when the error is not a failed transfer: a cancelled
 * upload, or an answer from denthub-documents itself (expired session, no access) that the caller
 * handles as before.
 */
export const getUploadFailureKind = (error: unknown): UploadFailureKind | undefined => {
    if (axios.isCancel(error) || !axios.isAxiosError(error)) return undefined;
    const status = error.response?.status;
    // No response: dropped connection, or a timeout answered without CORS headers. 408 is the
    // same timeout when the browser does get to see it.
    if (!status || status === 408) return 'CONNECTION';
    if (status === 413) return 'TOO_LARGE';
    if (status >= 500) return 'SERVER';
    return undefined;
};

const MESSAGE_KEYS: Record<UploadFailureKind, string> = {
    CONNECTION: 'UPLOAD_FAILED_CONNECTION',
    TOO_LARGE: 'UPLOAD_FAILED_TOO_LARGE',
    SERVER: 'UPLOAD_FAILED_SERVER',
};

/** Title and message keys (errors namespace) for the feedback dialog, with the upload's size. */
export const getUploadFailureFeedback = (kind: UploadFailureKind, files: File[]) => ({
    translationTitleKey: 'UPLOAD_FAILED_TITLE',
    translationMessageKey: MESSAGE_KEYS[kind],
    translationParams: { count: files.length, size: formatBytes(getTotalBytes(files)) },
});
